/**
 * Human-readable duration formatting, shared by anything that has to render
 * an elapsed time to a player (today: the profile's game-declared `duration`
 * statistics — APP_SPEC §43 — via `features/profile/format-stat.ts`).
 *
 * The output always carries explicit units. A bare clock reading like
 * `457:53` is ambiguous — a reader cannot tell minutes from seconds from
 * hours — and the profile shows a *record* (a personal best) rather than a
 * running timer, where a clock face would be the right idiom. So:
 *
 *   under a minute      42s
 *   under an hour       2m 32s
 *   an hour or more     1h 07m 18s
 *
 * Sub-minute values keep sub-second precision off deliberately: these are
 * whole-second records, and `27.473s` reads as instrumentation, not as an
 * achievement.
 *
 * This does not interpret or alter any stored value — the input unit is
 * milliseconds, exactly as the platform stores a `duration` metric.
 */
const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/** Formats a duration given in **milliseconds**. Negative input clamps to `0s`. */
export function formatDurationMs(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.round(milliseconds / MS_PER_SECOND));

  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  const totalMinutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);

  const pad = (value: number) => String(value).padStart(2, '0');

  if (hours > 0) return `${String(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  if (totalMinutes > 0) return `${String(minutes)}m ${pad(seconds)}s`;
  return `${String(seconds)}s`;
}
